import React, { FC, ReactNode } from "react";
import { TableInstance } from "react-table";
import { Table } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import "./DataTable.scss";
import { ReactComponent as ArrowSort } from "assets/images/iconsV2/arrow-narrow-up-blue.svg";
import { ReactComponent as ArrowUpDownIcon } from "assets/images/iconsV2/switch-vertical-01.svg";
import LoadingSkeleton from "components/atoms/LoadingSkeleton/LoadingSkeleton";
import Tablefooter from "../Tablefooter/Tablefooter";

export type DataTableV2Props = {
  isLoading?: boolean;
  tableInstance: TableInstance<any>;
  customNoResults?: ReactNode;
  isProcessing?: boolean;
  isLoadingMoreData?: boolean;
  processingMessage?: string;
  autoPagination?: boolean;
  RowSubComponent?: FC<{ row: any }>;
  onRowClick?: (row: any, e: React.MouseEvent<HTMLTableRowElement, MouseEvent>) => void;
  getRowClassName?: (row: any) => string;
  tableLayout?: string;
  includeTableFooter?: boolean;
  footerProps?: {
    totalPages: number;
    totalData: number;
    currentPage: number;
    itemsPerPage: number;
    setItemsPerPage: (size: number) => void;
    setCurrentPage: (page: number) => void;
  };
};

export default function DataTable({
  tableInstance,
  isLoading = false,
  customNoResults = "",
  isProcessing = false,
  isLoadingMoreData = false,
  processingMessage = "",
  autoPagination = false,
  RowSubComponent,
  onRowClick,
  getRowClassName,
  tableLayout,
  includeTableFooter,
  footerProps,
}: DataTableV2Props) {
  const { getTableProps, getTableBodyProps, headerGroups, rows, page, prepareRow, columns, visibleColumns, state } =
    tableInstance;

  const dataRows = autoPagination ? page : rows;
  const { totalPages, totalData, currentPage, itemsPerPage, setItemsPerPage, setCurrentPage } = footerProps || {
    totalPages: 0,
    totalData: 0,
    currentPage: 0,
    itemsPerPage: 0,
    setItemsPerPage: () => {},
    setCurrentPage: () => {},
  };

  return (
    <div className="table-container-v2 radius-md">
      <div style={{ overflowX: "auto" }}>
        <Table
          className={`table-v2 ${tableLayout === "fixed" ? "table-v2--table-fixed" : ""}`}
          id="data-grid"
          {...getTableProps()}
        >
          {/* Colgroup ensures fixed + auto-fill widths */}
          <colgroup>
            {visibleColumns.map((col) => (
              <col
                key={col.id}
                style={{
                  width: col.width ? col.width : undefined,
                }}
              />
            ))}
          </colgroup>

          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()} onClick={(e) => e.stopPropagation()}>
                {headerGroup.headers.map((column) => (
                  <th
                    {...column.getHeaderProps(
                      column.sortable
                        ? column.getSortByToggleProps({
                            title: `Sort by ${column.Header}`,
                          })
                        : undefined,
                    )}
                    className="text-xs text-secondary-v2 font-medium bg-tertiary"
                    style={{ textAlign: column.align ?? "left" }}
                  >
                    <div className="d-flex align-items-center justify-content-between">
                      <div style={{ textAlign: column.align ?? "left", flex: 1 }}>{column.render("Header")}</div>
                      <span>
                        {column.isSorted ? (
                          column.isSortedDesc ? (
                            <ArrowSort className="ml-2 table-v2-sort-icon" />
                          ) : (
                            <ArrowSort className="ml-2 table-v2-sort-icon" transform="rotate(180)" />
                          )
                        ) : (
                          column.sortable && <ArrowUpDownIcon className="ml-2" />
                        )}
                      </span>
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody {...getTableBodyProps()}>
            {dataRows.length > 0 && !isLoading ? (
              dataRows.map((row) => {
                prepareRow(row);
                return (
                  <React.Fragment key={row.getRowProps().key}>
                    <tr
                      {...row.getRowProps({
                        className: `${getRowClassName?.(row) || ""} bg-primary-hover ${onRowClick ? "cursor-pointer" : ""}`.trim(),
                      })}
                      onClick={(e) => onRowClick?.(row, e)}
                    >
                      {row.cells.map((cell) => (
                        <td
                          {...cell.getCellProps()}
                          className={`tbody-td text-sm text-primary font-regular ${state.pageSize === 100 ? "compact" : ""}`}
                          style={{ textAlign: cell.column.align ?? "left" }}
                        >
                          {cell.render("Cell")}
                        </td>
                      ))}
                    </tr>

                    {row.isExpanded && RowSubComponent && (
                      <tr className="row-subcomponent">
                        <td colSpan={visibleColumns.length} style={{ padding: 0 }}>
                          <RowSubComponent row={row} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            ) : isLoading ? (
              Array.from({ length: 2 }).map((_, index) => (
                <tr key={index}>
                  <td colSpan={columns.length} className="pt-0 pb-0 overflow-hidden">
                    <LoadingSkeleton rows={1} bodyClasses="p-0" className="p-4" noShadow />
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length} className="no-results">
                  {customNoResults || (
                    <FormattedMessage
                      id="components.organisms.listing.noResultsFound"
                      defaultMessage="No results found. Try adjusting your search or filter."
                    />
                  )}
                </td>
              </tr>
            )}

            {isProcessing && (
              <tr>
                <td colSpan={columns.length}>
                  <div className="d-flex align-items-center w-100 justify-content-end table-spinner mt-5">
                    <div className="spinner-border spinner-border-sm" role="status" />
                    <p className="ml-3">{processingMessage}</p>
                  </div>
                </td>
              </tr>
            )}

            {isLoadingMoreData && !isProcessing && (
              <tr>
                <td colSpan={columns.length} className="pt-0 pb-0 overflow-hidden">
                  <LoadingSkeleton rows={1} bodyClasses="p-0" className="p-0" noShadow />
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {includeTableFooter && (
        <Tablefooter
          totalPages={totalPages}
          totalData={totalData}
          currentPage={currentPage}
          itemsPerPage={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
          pageClicked={(page) => setCurrentPage(page)}
        />
      )}
    </div>
  );
}

DataTable.defaultProps = {
  isLoading: false,
  customNoResults: "",
  isProcessing: false,
  isLoadingMoreData: false,
  processingMessage: "",
  autoPagination: false,
  RowSubComponent: undefined,
  onRowClick: undefined,
  getRowClassName: undefined,
  tableLayout: undefined,
  includeTableFooter: false,
  footerProps: {
    totalPages: 0,
    totalData: 0,
    currentPage: 0,
    itemsPerPage: 0,
    setItemsPerPage: () => {},
    setCurrentPage: () => {},
  },
};
